import { gql } from '@apollo/client';
import { analyticArguments, analyticArgumentsQuery } from '../analyticArgument';

const ecommerceComparisonAnalyticArgumentsQuery = `
  $projectId: Id!,
  $comparisonId: Id!,
  $start: Timestamp!,
  $end: Timestamp!,
  $objectList: [Id!]!,
  $granularity: GranularityInput!,
  $timezone: Int!,
  $sentiment: [Sentiment!],
  $postOwnership: [PostOwnership!],
  $labelList: [Id!],
  $excludeLabelList: [Id!],
  $validationList: [StreamValidation!],
  $contentTypeList: [ContentType!],
  $includeKeywords: [String!],
  $excludeKeywords: [String!],
  $fromUsername: String!,
  $showEmptyLabel: Boolean,
  $replyToOriginalId: [String!],
  $sentimentConfidenceLevelMin:BigDecimal!
  $sentimentConfidenceLevelMax:BigDecimal!
  $queryStringBooleanOperator: QueryStringBooleanOperator
  $languageList: [String!]
  $userRating: [Int!]
  $retweetFilter: RetweetFilter
  $talkType: [TalkType!]
  $ageGeneration: [AgeGeneration!]
  `;

const ecommerceComparisonAnalyticArguments = `
  projectId: $projectId,
  comparisonId: $comparisonId,
  start: $start,
  end: $end,
  objectList: $objectList,
  granularity: $granularity,
  timezone: $timezone,
  sentiment: $sentiment,
  postOwnership: $postOwnership,
  labelList: $labelList,
  excludeLabelList: $excludeLabelList,
  validationList: $validationList,
  contentTypeList: $contentTypeList,
  includeKeywords: $includeKeywords,
  excludeKeywords: $excludeKeywords,
  fromUsername: $fromUsername,
  showEmptyLabel: $showEmptyLabel,
  replyToOriginalId: $replyToOriginalId,
  sentimentConfidenceLevelMin:$sentimentConfidenceLevelMin,
  sentimentConfidenceLevelMax:$sentimentConfidenceLevelMax,
  queryStringBooleanOperator: $queryStringBooleanOperator,
  languageList: $languageList,
  userRating: $userRating,
  retweetFilter: $retweetFilter,
  talkType: $talkType,
  ageGeneration: $ageGeneration
  `;

export const ecommerceResult = `
  originalId
  fromName
  content
  ecommerce
  price
  timeStamp
  review
  rating
  sentiment
  isValidated
  streamVisibility
  postOwnership
  link
  label {
    id
    name
    displayName
    colorType
    isActive
    createdAt
  }
  objectModel {
    id
    socialMedia
    streamType
    content
    extraContent
    objectName
    isHashtag
    createdAt
    openApiId
    displayPicture
    includes
    excludes
    languages
  }
  product {
    id
    name
    link
    originalId
    platform
    brand
    subBrand
  }
  `;

export const ECOMMERCE_GET_TOTAL_COMMENT = gql`
  query ecommerce_getEcommerceReview(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getEcommerceReview(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          recentValue
        growth
        pastValue
        contentType
        title
      }
  }`;

export const ECOMMERCE_GET_AVG_RATING = gql`
  query ecommerce_getAverageRating(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getAverageRating(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          recentValue
        growth
        pastValue
        contentType
        title
      }
  }`;

export const ECOMMERCE_GET_TOTAL_POSITIVE = gql`
  query ecommerce_getPositiveTalk(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getPositiveTalk(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          recentValue
        growth
        pastValue
        contentType
        title
      }
  }`;

export const ECOMMERCE_GET_TOTAL_NEGATIVE = gql`
  query ecommerce_getNegativeTalk(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getNegativeTalk(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          recentValue
        growth
        pastValue
        contentType
        title
      }
  }`;

export const ECOMMERCE_GET_TOTAL_PRODUCTS = gql`
  query ecommerce_getTotalProduct(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getTotalProduct(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          recentValue
        growth
        pastValue
        contentType
        title
      }
  }`;

export const ECOMMERCE_GET_CHRONOLOGICAL_PERFORMANCE = gql`
  query ecommerce_getChronologicalPerformance(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getChronologicalPerformance(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
        contentType
        values {
            timestamp
            value
        }
      }
  }`;

export const ECOMMERCE_GET_CHRONOLOGICAL_RATING = gql`
  query ecommerce_getChronologicalRating(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getChronologicalRating(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
        timeStamp
        oneStar
        twoStar
        threeStar
        fourStar
        fiveStar
        }
  }`;

export const ECOMMERCE_GET_CHRONOLOGICAL_REVIEWS = gql`
  query ecommerce_getChronologicalReviews(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getChronologicalReviews(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
        contentType
        values {
            timestamp
            value
        }
      }
  }`;

export const ECOMMERCE_GET_SENTIMENT_ANALYSIS = gql`
  query ecommerce_getSentimentAnalysis(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getSentimentAnalysis(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          name
          value
      }
  }`;

export const ECOMMERCE_GET_WORD_CLOUD = gql`
  query ecommerce_getWordCloud(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getWordCloud(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          name
          value
      }
  }`;

export const ECOMMERCE_GET_TOP_PEOPLE = gql`
  query ecommerce_getTopPeople(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getTopPeople(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          name
          value
      }
  }`;

export const ECOMMERCE_GET_COMPARISON_LIST = gql`
  query EcommerceComparison_getComparisonNamespaceList($projectId: Id!) {
    ecommerceComparison_getComparisonNamespaceList(projectId: $projectId) {
      id
      namespace
      displayName
      isDefault
      createdAt
      sourceType
      colors
    }
  }
`;

export const ECOMMERCE_GET_COMPARISON_POSITIONING = gql`
  query EcommerceComparison_getPositioningEcommerce(
    ${ecommerceComparisonAnalyticArgumentsQuery}
  ) {
    ecommerceComparison_getPositioningEcommerce(
     ${ecommerceComparisonAnalyticArguments}
    ) {
      name
      popularity
      positivity
    }
  }
`;

export const ECOMMERCE_GET_COMPARISON_CHRONOLOGICAL_RATING = gql`
  query EcommerceComparison_getChronologicalRating(
   ${ecommerceComparisonAnalyticArgumentsQuery}
  ) {
    ecommerceComparison_getChronologicalRating(
      ${ecommerceComparisonAnalyticArguments}
    ) {
      name
      values {
        timestamp
        value
      }
    }
  }
`;

export const ECOMMERCE_GET_COMPARISON_POPULARITY = gql`
  query EcommerceComparison_getPopularityEcommerce(
   ${ecommerceComparisonAnalyticArgumentsQuery}
  ) {
    ecommerceComparison_getPopularityEcommerce(
       ${ecommerceComparisonAnalyticArguments}
    ) {
      name
      value
    }
  }
`;

export const ECOMMERCE_GET_COMPARISON_SENTIMENT = gql`
  query EcommerceComparison_getSentimentEcommerce(
    ${ecommerceComparisonAnalyticArgumentsQuery}
  ) {
    ecommerceComparison_getSentimentEcommerce(
       ${ecommerceComparisonAnalyticArguments}
    ) {
      groupName
      positive
      negative
      neutral
    }
  }
`;

export const ECOMMERCE_GET_COMPARISON_ADVANCE_METRIC = gql`
  query EcommerceComparison_getAdvanceMetric(
    ${ecommerceComparisonAnalyticArgumentsQuery}
  ) {
    ecommerceComparison_getAdvanceMetric(
     ${ecommerceComparisonAnalyticArguments}
    ) {
      name
      metricOrder
      values {
        isActive
        computationName
        value
        isPercentage
        isEligible
      }
    }
  }
`;

export const ECOMMERCE_GET_STREAM = gql`
  query ecommerce_getStreamEcommerce(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $columnName: SortingByColumn,
    $sortingMethod: SortingType,
    $page: Int!,
    $limit: Int!,
    $searchType: SearchType
    ) {
      ecommerce_getStreamEcommerce(
        ${analyticArguments},
        columnName: $columnName,
        sortingMethod: $sortingMethod,
        page: $page,
        limit: $limit,
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          totalRows
        totalPages
        page
        limit
        result {
           ${ecommerceResult}
        }
      }
  }`;

export const ECOMMERCE_GET_IGNORE_STREAM = gql`
  query ecommerce_getIgnoredStreamEcommerce(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $columnName: SortingByColumn,
    $sortingMethod: SortingType,
    $page: Int!,
    $limit: Int!,
    $searchType: SearchType
    ) {
      ecommerce_getIgnoredStreamEcommerce(
        ${analyticArguments},
        columnName: $columnName,
        sortingMethod: $sortingMethod,
        page: $page,
        limit: $limit,
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          totalRows
        totalPages
        page
        limit
        result {
           ${ecommerceResult}
        }
      }
  }`;

export const ECOMMERCE_GET_OBJECTS = gql`
  query ecommerce_getObject($projectId: Id!) {
    ecommerce_getObject(projectId: $projectId) {
      id
      socialMedia
      streamType
      content
      extraContent
      objectName
      isHashtag
      createdAt
      openApiId
      displayPicture
      includes
      excludes
      languages
      isEntity
      entityName
      isAuthorizeTiktok
      status
      errorMessage
    }
  }
`;

export const ECOMMERCE_GET_PRODUCT_GROUPS = gql`
  query ecommerceProductManagement_getGroupProduct(
    $projectId: Id!
    $name: String
    $depth: String
  ) {
    ecommerceProductManagement_getGroupProduct(
      projectId: $projectId
      name: $name
      depth: $depth
    ) {
      id
      name
      category
      subBrands {
        id
        name
        category
        products {
          id
          name
          originalId
          platform
          image
        }
      }
    }
  }
`;

export const ECOMMERCE_GET_PRODUCTS = gql`
  query ecommerceProductManagement_getProducts(
    $projectId: Id!
    $objectIds: [Id!]
    $name: String
  ) {
    ecommerceProductManagement_getProducts(
      projectId: $projectId
      objectIds: $objectIds
      name: $name
    ) {
      id
      name
      originalId
      platform
      image
    }
  }
`;

export const ECOMMERCE_GET_STREAM_COMMENTING_TO_PRODUCT = gql`
  query EcommerceProductManagement_getProductHierarchy(
    $projectId: Id!
    $objectIds: [Id!]
    $name: String
  ) {
    ecommerceProductManagement_getProductHierarchy(
      projectId: $projectId
      objectIds: $objectIds
      name: $name
    ) {
      id
      name
      category
      subBrands {
        id
        name
        category
        products {
          id
          name
          originalId
          platform
          link
          image
        }
      }
    }
  }
`;

export const ECOMMERCE_AUTO_LABEL_LIST = gql`
  query ecommerceAutoLabel_getAutoLabelList($projectId: Id!) {
    ecommerceAutoLabel_getAutoLabelList(projectId: $projectId) {
      id
      projectId
      name
      description
      start
      postOwnerships
      keywords
      isActive
      createdAt
      productList {
        id
        name
        originalId
        platform
        link
        image
      }
    }
  }
`;

export const ECOMMERCE_AUTO_LABEL_BY_ID = gql`
  query ecommerceAutoLabel_getAutoLabelById(
    $projectId: Id!
    $autoLabelId: Id!
  ) {
    ecommerceAutoLabel_getAutoLabelById(
      projectId: $projectId
      autoLabelId: $autoLabelId
    ) {
      id
      projectId
      objectList {
        id
        socialMedia
        streamType
        content
        extraContent
        objectName
      }
      name
      description
      start
      labelList {
        id
        name
        displayName
        colorType
        isActive
        createdAt
      }
      postOwnerships
      keywords
      isActive
      createdAt
      productList {
        id
        name
        originalId
        platform
        link
        image
      }
    }
  }
`;

export const ECOMMERCE_GET_COMPARISON_ENTITY = gql`
  query ecommerceComparison_getComparisonEntity($comparisonId: Id!) {
    ecommerceComparison_getComparisonEntity(comparisonId: $comparisonId) {
      id
      namespace
      displayName
      createdAt
      colors
      groups {
        id
        name
        displayName
        objectGroup {
          id
          name
          displayName
          createdAt
          objectList {
            id
            socialMedia
            streamType
            extraContent
            objectName
            isHashtag
          }
        }
        objectList {
          id
          socialMedia
          streamType
          extraContent
          objectName
          isHashtag
        }
        labelList {
          id
          name
          displayName
          colorType
        }
        productGroup {
          id
          name
          category
        }
        createdAt
      }
    }
  }
`;

export const ECOMMERCE_GET_TOP_PRODUCT = gql`
  query ecommerce_getTopProduct(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getTopProduct(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          id
          name
          platform
          comment
          rating
      }
  }`;

export const ECOMMERCE_GET_TOP_STORE = gql`
  query ecommerce_getTopStore(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getTopStore(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          id
          name
          platform
          product
          comment
          rating
      }
  }`;

export const ECOMMERCE_GET_TOTAL_RATING = gql`
  query ecommerce_getTotalRating(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getTotalRating(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          name
          value
      }
  }`;

export const ECOMMERCE_GET_SENTIMENT_VS_RATING = gql`
  query ecommerce_getSentimentVsRating(
    ${analyticArgumentsQuery},
    $retweetFromOriginalIdList: [String!],
    $searchType: SearchType
    ) {
      ecommerce_getSentimentVsRating(
        ${analyticArguments},
        retweetFromOriginalIdList: $retweetFromOriginalIdList,
        searchType: $searchType
        ) {
          name
          platform
          rating
          sentimentScore
      }
  }`;
